/**
 * Level 3 — Festival Market (memory match: the 6 ENERGY letter tiles + the
 * value each letter stands for). The value's description shows once matched.
 */
import tileE from "./stage3/client/e.png";
import tileN from "./stage3/client/n.png";
import tileE2 from "./stage3/client/e2.png";
import tileR from "./stage3/client/r.png";
import tileG from "./stage3/client/g.png";
import tileY from "./stage3/client/y.png";
import deskBg from "./stage3/stage3_desk.png";
import mobBg from "./stage3/stage3_mob.png";
import cardFront from "./stage3/front.png";
import cardBack from "./stage3/back.png";

export { deskBg as FESTIVAL_MARKET_DESK_BG, mobBg as FESTIVAL_MARKET_MOB_BG };
export { cardFront as CARD_FRONT_IMG, cardBack as CARD_BACK_IMG };

export const FESTIVAL_MARKET_PUZZLE_ID = "festival_market";

/** Points written to DB `stage3` when all pairs are matched. */
/* Kept at 40 so the journey still totals 220; the live counter adds them pair by pair. */
export const FESTIVAL_MARKET_POINTS = 40;

/** Journey completion after this checkpoint (3 of 5). */
export const FESTIVAL_MARKET_COMPLETION_PERCENT = 60;

/** How long a wrong pair stays face up before flipping back. */
export const MISMATCH_DELAY_MS = 900;

/* `item` names the tile for screen readers; the two E tiles differ only by colour. */
export const FESTIVAL_MARKET_PAIRS = [
  {
    id: "entrepreneurial",
    item: "E, yellow",
    image: tileE,
    meaning: "Entrepreneurial Excellence",
    learning: {
      heading: "Entrepreneurial Excellence",
      text: "Once risks are evaluated and the decision is made, execution is a synchronized effort.",
    },
  },
  {
    id: "nurturing",
    item: "N, blue",
    image: tileN,
    meaning: "Nurturing Openness & Boldness",
    learning: {
      heading: "Nurturing Openness & Boldness",
      text: "Create a safe & trusted space for expression and innovation.",
    },
  },
  {
    id: "empowerment",
    item: "E, purple",
    image: tileE2,
    meaning: "Effective Empowerment",
    learning: {
      heading: "Effective Empowerment",
      text: "Enable people through inclusion, equal opportunity & conscious delegation.",
    },
  },
  {
    id: "rally",
    item: "R, green",
    image: tileR,
    meaning: "Rally together for P²S",
    learning: {
      heading: "Rally together for P²S",
      text: "Rally around Performance & Productivity.",
    },
  },
  {
    id: "growing",
    item: "G, brown",
    image: tileG,
    meaning: "Growing Talent",
    learning: {
      heading: "Growing Talent",
      text: "Build Competence, create fungible & mobile talent. Unleashing the specialists/ mavericks to add value.",
    },
  },
  {
    id: "youthful",
    item: "Y, red",
    image: tileY,
    meaning: "Youthful Energy",
    learning: {
      heading: "Youthful Energy",
      text: "Blending wisdom & experience with passion and youthful energy.",
    },
  },
];

export const FESTIVAL_MARKET_STORY = {
  heading: "Every Letter Has a Meaning",
  quote: "“Together, our values spell ENERGY.”",
  paragraphs: [
    "Your challenge is to match each ENERGY letter with the value it stands for. Every correct match reveals what that value means.",
    "Complete every match to earn the Key of Symbolism.",
  ],
  objective: "Match every ENERGY letter with the value it stands for.",
  button: "Start Matching",
};

export const FESTIVAL_MARKET_RETRY = {
  heading: "Try Another Match",
  text: "Take your time and remember which value each coloured letter stands for. Every correct match brings you closer to the Grand Celebration.",
};

export const FESTIVAL_MARKET_SUCCESS = {
  heading: "All Pairs Matched!",
  unlocked: "🔓 Congratulations! You've Unlocked the ENERGY of Diwali! 🔓",
  text: "Every letter of ENERGY stands for a value, and you've discovered the meaning behind each one.",
  reward: "By understanding these values, you've earned the Key of Symbolism.",
  next: "Return to the Journey Map and continue toward the Celebration Steps.",
  button: "Continue Journey",
};

const PAIR_IDS = new Set(FESTIVAL_MARKET_PAIRS.map((p) => p.id));
const CACHE_KEY = "festival_market_progress";

/** Small deterministic PRNG (mulberry32) so a seed always gives the same board. */
function mulberry32(seed) {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

export function newBoardSeed() {
  return Math.floor(Math.random() * 2 ** 31);
}

/** The 12 cards in board order for `seed`: { id: "item:rally" | "meaning:rally", kind, pair }. */
export function buildBoard(seed) {
  const cards = FESTIVAL_MARKET_PAIRS.flatMap((p) => [
    { id: `item:${p.id}`, kind: "item", pair: p },
    { id: `meaning:${p.id}`, kind: "meaning", pair: p },
  ]);
  const rand = mulberry32(seed);
  for (let i = cards.length - 1; i > 0; i -= 1) {
    const j = Math.floor(rand() * (i + 1));
    [cards[i], cards[j]] = [cards[j], cards[i]];
  }
  return cards;
}

/** Normalise { seed, matched } from the server, DB or cache. */
export function cleanProgress(value) {
  const matched = Array.isArray(value?.matched)
    ? [...new Set(value.matched.filter((id) => PAIR_IDS.has(id)))]
    : [];
  const seed = Number.isInteger(value?.seed) && value.seed >= 0 ? value.seed : null;
  return { seed, matched };
}

/** Progress from a stages row (`stage3_ans`); empty if none / other puzzle. */
export function readProgressFromStage(stage) {
  let ans = stage?.stage3_ans;
  if (typeof ans === "string") {
    try {
      ans = JSON.parse(ans);
    } catch {
      ans = null;
    }
  }
  if (!ans || ans.puzzle !== FESTIVAL_MARKET_PUZZLE_ID) return { seed: null, matched: [] };
  return cleanProgress(ans);
}

/** Session cache: keeps the board seed (and demo progress) across a refresh. */
export function readCachedProgress() {
  try {
    return cleanProgress(JSON.parse(sessionStorage.getItem(CACHE_KEY) || "null"));
  } catch {
    return { seed: null, matched: [] };
  }
}

export function writeCachedProgress(progress) {
  try {
    sessionStorage.setItem(CACHE_KEY, JSON.stringify(cleanProgress(progress)));
  } catch {
    /* storage unavailable: DB remains the source of truth */
  }
}

/** Server check of a flipped pair → { match }. Throws on network / server error. */
export async function submitFestivalMatch({
  backendBase,
  adminToken,
  storedUser,
  stageId,
  first,
  second,
  seed,
}) {
  if (!backendBase || !adminToken) throw new Error("Match service unavailable");
  const res = await fetch(`${backendBase}/festivalMarketMatch`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${adminToken}`,
    },
    body: JSON.stringify({
      userId: storedUser?.userId || storedUser?.userid || storedUser?.id,
      stageId: stageId || undefined,
      first,
      second,
      seed,
    }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok || !data.success) {
    throw new Error(data.message || `Match check failed (${res.status})`);
  }
  return { match: Boolean(data.match) };
}

/**
 * Sound-effect hook. Stage 8 wires audio by listening for `jnb:sfx`
 * (detail = "card-flip" | "match" | "mismatch").
 */
export function playSfx(name) {
  try {
    window.dispatchEvent(new CustomEvent("jnb:sfx", { detail: name }));
  } catch {
    /* no-op outside the browser */
  }
}
